import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { PlusIcon } from '@heroicons/react/20/solid';
import { ChevronLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Field, FieldArray, Form, Formik } from 'formik';
import styled from 'styled-components';
import Spinner from 'ui-kit/Spinner';
import { colors } from 'ui-kit/theme/colors';
import * as Yup from 'yup';

import { StepTitle } from '../../../pages/ScenariosGeneratorPage/ScenariosGeneratorPage';

const validationSchema = Yup.object().shape({
  characters: Yup.array()
    .of(
      Yup.string()
        .required("Character's name can't be empty")
        .matches(/^[A-Za-z]+$/, 'Only symbols A-Z are allowed')
    )
    .min(1, 'Add at least one character')
});

export const CharactersNamesTool = (props) => {
  const {
    initialCharactersNames = [],
    onContinue = () => { },
    onBack = () => { },
    onChange = () => { }
  } = props;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    onChange(initialCharactersNames);
  }, [initialCharactersNames]);

  return (
    <Wrapper>
      <StepTitle>Character names</StepTitle>
      <Formik
        initialValues={{ characters: initialCharactersNames }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          if (loading) return;
          setLoading(true);
          await onContinue(values.characters);
          setLoading(false);
        }}
      >
        {({ values, errors, touched }) => (
          <Form>
            <FormWrapper>
              <FieldArray name="characters">
                {({ push, remove }) => (
                  <>
                    {values.characters.map((_, index) => (
                      <div key={`character_name_${index}`}>
                        <FieldRow>
                          <FieldWrapper>
                            <Field
                              name={`characters.${index}`}
                              placeholder={`${index + 1} character's name`}
                              as={Input}
                            />
                          </FieldWrapper>
                          {values.characters.length > 1 && (
                            <DeleteIconWrapper
                              onClick={() => remove(index)}
                            >
                              <TrashIcon />
                            </DeleteIconWrapper>
                          )}
                        </FieldRow>
                        {errors.characters?.[index] && touched.characters?.[index] ? (
                          <ErrorMessage>{errors.characters[index]}</ErrorMessage>
                        ) : (
                          <Description>Please provide character names</Description>
                        )}
                      </div>
                    ))}
                    <AddCharacterButton
                      type="button"
                      onClick={() => push('')}
                    >
                      <PlusIcon className='w-4 h-4 mr-2' />
                      Add a character
                    </AddCharacterButton>
                  </>
                )}
              </FieldArray>

              <ButtonPlaceholder>
                {isMobile ? (
                  <MobileBackButton
                    type="button"
                    onClick={onBack}
                  >
                    <ChevronLeftIcon />
                  </MobileBackButton>
                ) : (
                  <TransparentButton
                    type="button"
                    onClick={onBack}
                  >
                    Back
                  </TransparentButton>
                )}

                <OrangeButton isMobile={isMobile} type="submit">
                  {loading ? <Spinner /> : 'Continue'}
                </OrangeButton>
              </ButtonPlaceholder>
            </FormWrapper>
          </Form>
        )}
      </Formik>
    </Wrapper >
  );
};

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Description = styled.span`
    color: rgba(169, 169, 169, 1);
    font-family: Nunito Sans;
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    text-align: left;
    margin-top: 8px;
    display: block;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: 768px) {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    margin-bottom: 40px;
  }
`;

const FieldRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`;

const DeleteIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 24px;
    height: 24px;
    color: #6c6c6c;
    transition: color 0.2s ease;
    
    &:hover {
        color: #a3a3a3;
    }
`;

const MobileBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 40px;
  background: #1A1A1A;
  border-radius: 100px;
  color: white;
  border: 1px solid #FF7E55;
  cursor: pointer;
  padding: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;
const Input = styled.input`
  background: rgba(19, 19, 19, 1);
  border-radius: 15px;
  color: white;
  font-size: 16px;
  line-height: 18px;
  outline: none;
  padding: 16px;
  border: 1px solid transparent;
  width: 100%;

  :focus {
    border-color: #FF7E55;
    box-shadow: none !important;
  }

  ::placeholder {
    color: rgba(105, 105, 105, 1);
  }
`;

const AddCharacterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #FF7E55;
  color: #FF7E55;
  background-color: transparent;
  border-radius: 8px;
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  width: 158px;
  height: 30px;
  padding: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 8px 0 16px 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ButtonPlaceholder = styled.div`
    width: 100%;
    display: flex;
    gap: 16px;
    
    @media (max-width: 768px) {
      margin-top: auto;
      background: #1A1A1A;
      padding-top: 10px;
      padding-bottom: 16px;
      bottom: 0;
      position: fixed;
      width: 100%;
      z-index: 10;
    
    }
`;

const OrangeButton = styled.button`
    background-color: rgba(255, 126, 85, 1);
    width: ${props => props.isMobile ? '66%' : '100%'};
    padding: 8px 24px;
    border-radius: 100px;
    font-family: Nunito Sans;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    box-shadow: 0px 12px 40px 0px rgba(255, 126, 85, 0.12);
    display: flex;
    justify-content: center;
`;

const TransparentButton = styled(OrangeButton)`
    background-color: transparent;
    border: 1px solid rgba(255, 126, 85, 1);
    color: rgba(255, 126, 85, 1);
    box-shadow: none;
`;

const FieldWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const ErrorMessage = styled(Description)`
  color: ${colors.error};
`;